import axios from 'axios';
import { toApiDateEnd, toApiDateStart } from '../utils/datetimeLocal';
import {
  clearAuth,
  getAuthSlug,
  getAuthToken,
  getRefreshToken,
  setAuthSession,
} from './authStorage';

function appendSort(params, ordenar, direcao) {
  if (ordenar) params.set('ordenar', ordenar);
  if (direcao) params.set('direcao', direcao);
}

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  if (config.data instanceof FormData) {
    delete config.headers['Content-Type'];
  }
  return config;
});

const isAuthBypassRequest = (config) => {
  const path = (config?.url || '').split('?')[0];
  // Rotas públicas (ex.: portal do fornecedor) usam o próprio accessToken e nunca devem redirecionar ao login.
  if (/^\/v1\/public\//i.test(path)) return true;
  return /\/account\/(login|refresh|logout)$/i.test(path);
};

const loginPath = () => {
  const slug = getAuthSlug();
  const limpo = (slug || '').trim().toLowerCase();
  return limpo ? `/${limpo}/login` : '/';
};

let refreshPromise = null;

export async function refreshAccessToken() {
  const refreshToken = getRefreshToken();
  const slug = getAuthSlug();
  if (!refreshToken || !slug) {
    throw new Error('Sem refresh token');
  }
  if (!refreshPromise) {
    refreshPromise = api
      .post(`/v1/${slug}/account/refresh`, { refreshToken })
      .then((response) => {
        const data = response.data || {};
        if (!data.token) throw new Error('Refresh sem token');
        setAuthSession({
          token: data.token,
          refreshToken: data.refreshToken || refreshToken,
          slug: data.slug || slug,
        });
        return data.token;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

export async function logoutSession() {
  const refreshToken = getRefreshToken();
  const slug = getAuthSlug();
  if (slug && refreshToken) {
    await api.post(`/v1/${slug}/account/logout`, { refreshToken });
  }
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    if (
      error.response?.status === 401 &&
      original &&
      !isAuthBypassRequest(original) &&
      !original._retry
    ) {
      original._retry = true;
      try {
        const token = await refreshAccessToken();
        original.headers = original.headers || {};
        original.headers.Authorization = `Bearer ${token}`;
        return api(original);
      } catch (refreshError) {
        console.warn('Sessão expirada. Redirecionando para login...', refreshError);
        clearAuth();
        window.location.href = loginPath();
      }
    }
    return Promise.reject(error);
  }
);

// Funções de API

// --- Account (Autenticação) ---
export const login = async (dominio, email, senha) => {
  return api.post(`/v1/${dominio}/account/login`, { email, senha });
};

// --- Usuários ---
export const createUser = async (usuarioData) => {
  return api.post('/v1/usuarios', usuarioData);
};

export const getAllUsers = async () => {
  return api.get('/v1/usuarios');
};

export const getUserById = async (id) => {
  return api.get(`/v1/usuarios/${id}`);
};

export const updateUser = async (id, usuarioData) => {
  return api.put(`/v1/usuarios/${id}`, usuarioData);
};

export const getMeuTema = () => api.get('/v1/usuarios/me/tema');
export const updateMeuTema = (payload) => api.put('/v1/usuarios/me/tema', payload);

export const deleteUser = async (id) => {
  return api.delete(`/v1/usuarios/${id}`);
};
export const updateUserStatus = (id, ativo) =>
  api.patch(`/v1/usuarios/${id}/status`, { ativo });

// --- Grupos de permissões ---
export const getPermissionProfiles = () => api.get('/v1/perfis-permissao');
export const getPermissionProfile = (id) => api.get(`/v1/perfis-permissao/${id}`);
export const createPermissionProfile = (payload) =>
  api.post('/v1/perfis-permissao', payload);
export const updatePermissionProfile = (id, payload) =>
  api.put(`/v1/perfis-permissao/${id}`, payload);
export const deletePermissionProfile = (id) =>
  api.delete(`/v1/perfis-permissao/${id}`);

// --- Produtos ---
export const createProduct = async (produtoData) => {
  return api.post('/v1/produtos', produtoData);
};

// acrescente params de paginação e (opcional) filtros
export const getAllProducts = async ({
  indice = 1,
  qtde = 20,
  termo = "",
  categoriaId = "",
  subCategoriaId = "",
  marcaId = "",
  fornecedorId = "",
  ativo = "",
  estabelecimentoId = "",
  eanDe = "",
  eanAte = "",
  ordenar = "",
  direcao = "",
} = {}) => {
  const params = new URLSearchParams();
  params.set("indice", indice);
  params.set("qtde", qtde);
  if (termo) params.set("termo", termo);
  if (categoriaId) params.set("categoriaId", categoriaId);
  if (subCategoriaId) params.set("subCategoriaId", subCategoriaId);
  if (marcaId) params.set("marcaId", marcaId);
  if (fornecedorId) params.set("fornecedorId", fornecedorId);
  if (ativo !== "" && ativo !== null && ativo !== undefined) params.set("ativo", ativo);
  if (estabelecimentoId) params.set("estabelecimentoId", estabelecimentoId);
  if (eanDe) params.set("eanDe", eanDe);
  if (eanAte) params.set("eanAte", eanAte);
  appendSort(params, ordenar, direcao);
  return api.get(`/v1/produtos?${params.toString()}`);
};

export const copiarPrecosEstabelecimento = (payload) =>
  api.post('/v1/produtos-preco/copiar-estabelecimento', payload);



export const getProductById = async (id, estabelecimentoId = null) => {
  const params = estabelecimentoId ? `?estabelecimentoId=${estabelecimentoId}` : '';
  return api.get(`/v1/produtos/${id}${params}`);
};

export const updateProduct = async (id, produtoData) => {
  return api.put(`/v1/produtos/${id}`, produtoData);
};

export const deleteProduct = async (id) => {
  return api.delete(`/v1/produtos/${id}`);
};

export const getTabelasPreco = () => api.get('/v1/tabelas-preco');

export const previewReajusteProdutos = (body) => api.post('/v1/produtos/reajuste/preview', body);

export const aplicarReajusteProdutos = (body) => api.post('/v1/produtos/reajuste', body);

// --- Categorias ---
export const createCategory = async (categoriaData) => {
  return api.post('/v1/categorias', categoriaData);
};

export const getAllCategories = async ({
  indice = 1,
  qtde = 20,
  termo = "",
  ordenar = "",
  direcao = "",
} = {}) => {
  const params = new URLSearchParams();
  params.set("indice", indice);
  params.set("qtde", qtde);
  if (termo) params.set("termo", termo);
  appendSort(params, ordenar, direcao);
  return api.get(`/v1/categorias?${params.toString()}`);
};

export const getCategoryById = async (id) => {
  return api.get(`/v1/categorias/${id}`);
};

export const updateCategory = async (id, categoriaData) => {
  return api.put(`/v1/categorias/${id}`, categoriaData);
};

export const deleteCategory = async (id) => {
  return api.delete(`/v1/categorias/${id}`);
};

// --- SubCategorias ---
export const createSubCategory = async (subCategoriaData) => {
  return api.post('/v1/subcategorias', subCategoriaData);
};

export const getAllSubCategories = async ({
  indice = 1,
  qtde = 20,
  termo = "",
  categoriaId = "",
  ordenar = "",
  direcao = "",
} = {}) => {
  const params = new URLSearchParams();
  params.set("indice", indice);
  params.set("qtde", qtde);
  if (termo) params.set("termo", termo);
  if (categoriaId) params.set("categoriaId", categoriaId);
  appendSort(params, ordenar, direcao);
  return api.get(`/v1/subcategorias?${params.toString()}`);
};

export const getSubCategoryById = async (id) => {
  return api.get(`/v1/subcategorias/${id}`);
};

export const updateSubCategory = async (id, subCategoriaData) => {
  return api.put(`/v1/subcategorias/${id}`, subCategoriaData);
};

export const deleteSubCategory = async (id) => {
  return api.delete(`/v1/subcategorias/${id}`);
};

// Grupos tributários
export const createTributaryGroup = async (grupoTributarioData) => {
  return api.post('/v1/grupostributarios', grupoTributarioData);
};

export const getAllTributaryGroups = async ({
  indice = 1,
  qtde = 20,
  termo = "",
  ordenar = "",
  direcao = "",
} = {}) => {
  const params = new URLSearchParams();
  params.set("indice", indice);
  params.set("qtde", qtde);
  if (termo) params.set("termo", termo);
  appendSort(params, ordenar, direcao);
  return api.get(`/v1/grupostributarios?${params.toString()}`);
};

export const getTributaryGroupById = async (id) => {
  return api.get(`/v1/grupostributarios/${id}`)
};

export const deleteTributaryGroup = async (id) => {
  return api.delete(`/v1/grupostributarios/${id}`);
};

export const updateTributaryGroup = async (id, grupoTributarioData) => {
  return api.put(`/v1/grupostributarios/${id}`, grupoTributarioData);
};

export const getTributaryGroupUfs = (id, regime) =>
  api.get(`/v1/grupostributarios/${id}/uf`, { params: { regime } });

export const upsertTributaryGroupUf = (id, regime, uf, payload) =>
  api.put(`/v1/grupostributarios/${id}/uf/${regime}/${uf}`, payload);

export const deleteTributaryGroupUf = (id, regime, uf) =>
  api.delete(`/v1/grupostributarios/${id}/uf/${regime}/${uf}`);

// --- Configuração fiscal por estabelecimento ---
export const getConfiguracao = (estabelecimentoId) =>
  api.get(`/v1/estabelecimentos/${estabelecimentoId}/configuracao-fiscal`);
export const updateConfiguracao = (estabelecimentoId, payload) =>
  api.put(`/v1/estabelecimentos/${estabelecimentoId}/configuracao-fiscal`, payload);
export const getCertificadoFiscal = (estabelecimentoId) =>
  api.get(`/v1/estabelecimentos/${estabelecimentoId}/configuracao-fiscal/certificado`);
export const uploadCertificadoFiscal = (estabelecimentoId, arquivo, senha) => {
  const form = new FormData();
  form.append('Arquivo', arquivo);
  form.append('Senha', senha);
  return api.post(
    `/v1/estabelecimentos/${estabelecimentoId}/configuracao-fiscal/certificado`,
    form,
  );
};

// --- Catálogo fiscal IBS/CBS ---
export const getCstsIbsCbs = () => api.get('/v1/fiscal/csts-ibs-cbs');
export const getClassesTributarias = (cst) => {
  const params = new URLSearchParams();
  if (cst) params.set('cst', cst);
  const qs = params.toString();
  return api.get(`/v1/fiscal/classes-tributarias${qs ? `?${qs}` : ''}`);
};
export const getNfesGerenciais = (params = {}) =>
  api.get('/v1/fiscal/nfes', { params });
export const getNfeGerencial = (id) =>
  api.get(`/v1/fiscal/nfes/${id}`);
export const prepararNfeDeVenda = (vendaId) =>
  api.get(`/v1/fiscal/nfes/preparar-venda/${vendaId}`);
export const emitirNfeGerencial = (payload) =>
  api.post('/v1/fiscal/nfes', payload);
export const excluirNfeGerencialRascunho = (id) =>
  api.delete(`/v1/fiscal/nfes/${id}`);
export const reenviarNfeGerencial = (id, { avancarNumeracao = false } = {}) =>
  api.post(`/v1/fiscal/nfes/${id}/reenviar`, { avancarNumeracao });
export const cancelarNfeGerencial = (id, justificativa) =>
  api.post(`/v1/fiscal/nfes/${id}/cancelar`, { justificativa });
export const cartaCorrecaoNfe = (id, textoCorrecao, sequencia = 1) =>
  api.post(`/v1/fiscal/nfes/${id}/carta-correcao`, { textoCorrecao, sequencia });
export const inutilizarNumeracaoFiscal = (payload) =>
  api.post('/v1/fiscal/inutilizar', payload);
export const cancelarNfceVenda = (vendaId, justificativa) =>
  api.post(`/v1/vendas/${vendaId}/nfce/cancelar`, { justificativa });
export const reemitirNfceVenda = (vendaId, { emitirEmContingencia = false, avancarNumeracao = false } = {}) =>
  api.post(`/v1/vendas/${vendaId}/nfce/reemitir`, { emitirEmContingencia, avancarNumeracao });
export const getPdvErroLogs = (params = {}) =>
  api.get('/v1/pdv/erros', { params });
export const downloadNfceVendaPdf = (vendaId) =>
  api.get(`/v1/vendas/${vendaId}/nfce/pdf`, { responseType: 'blob' });
export const downloadNfceVendaXml = (vendaId) =>
  api.get(`/v1/vendas/${vendaId}/nfce/xml`, { responseType: 'blob' });
export const downloadNfeGerencialPdf = (id) =>
  api.get(`/v1/fiscal/nfes/${id}/pdf`, { responseType: 'blob' });
export const downloadNfeGerencialXml = (id) =>
  api.get(`/v1/fiscal/nfes/${id}/xml`, { responseType: 'blob' });
export const enviarNfeGerencialWhatsApp = (id, telefone, { incluirXml = true } = {}) =>
  api.post(`/v1/fiscal/nfes/${id}/whatsapp`, { telefone, incluirXml });
export const enviarNfeGerencialEmail = (id, payload) =>
  api.post(`/v1/fiscal/nfes/${id}/email`, payload);
export const getEmailStatus = () => api.get('/v1/email/status');
export const conectarEmailGmail = (payload = {}) =>
  api.post('/v1/email/conectar', payload);
export const desconectarEmailGmail = () =>
  api.post('/v1/email/desconectar');
export const salvarEmailSmtp = (payload) =>
  api.put('/v1/email/smtp', payload);
export const removerEmailSmtp = () =>
  api.delete('/v1/email/smtp');
export const getCfopsFiscais = (params = {}) =>
  api.get('/v1/fiscal/cfops', { params });
export const saveCfopFiscal = (item) =>
  item._editing
    ? api.put(`/v1/fiscal/cfops/${item.codigo}`, item)
    : api.post('/v1/fiscal/cfops', item);
export const deleteCfopFiscal = (codigo) =>
  api.delete(`/v1/fiscal/cfops/${codigo}`);
export const getIbptFiscal = (params = {}) =>
  api.get('/v1/fiscal/ibpt', { params });
export const saveIbptFiscal = (item) =>
  item.id
    ? api.put(`/v1/fiscal/ibpt/${item.id}`, item)
    : api.post('/v1/fiscal/ibpt', item);
export const deleteIbptFiscal = (id) =>
  api.delete(`/v1/fiscal/ibpt/${id}`);

// Função para obter subcategorias com base na categoria
export const getSubCategoriesByCategory = async (categoryId) => {
  return api.get(`/v1/subcategorias/categoria/${categoryId}`);
};

// Função para obter produtos com base na subcategoria
export const getProductsBySubCategory = (subCategoryId) => {
  return api.get(`/v1/produtos/subcategoria/${subCategoryId}`);
};
export const getModeloEtiquetaById = async (id) => {
  return api.get(`/v1/modelosetiqueta/${id}`);
};

export const updateModeloEtiqueta = async (id, modeloEtiquetaData) => {
  return api.put(`/v1/modelosetiqueta/${id}`, modeloEtiquetaData);
};

export const deleteModeloEtiqueta = async (id) => {
  return api.delete(`/v1/modelosetiqueta/${id}`);
};


// --- Marcas ---
export const createBrand = async (brandData) => {
  return api.post('/v1/marcas', brandData);
};

export const getAllBrands = async ({
  indice = 1,
  qtde = 20,
  termo = "",
  ordenar = "",
  direcao = "",
} = {}) => {
  const params = new URLSearchParams();
  params.set("indice", indice);
  params.set("qtde", qtde);
  if (termo) params.set("termo", termo);
  appendSort(params, ordenar, direcao);
  return api.get(`/v1/marcas?${params.toString()}`);
};

export const getBrandById = async (id) => {
  return api.get(`/v1/marcas/${id}`);
};

export const updateBrand = async (id, brandData) => {
  return api.put(`/v1/marcas/${id}`, brandData);
};

export const deleteBrand = async (id) => {
  return api.delete(`/v1/marcas/${id}`);
};

// --- Clientes (paginado + busca) ---
export const getAllClients = async ({
  indice = 1,
  qtde = 20,
  termo = "",
  ativo = "",
  ordenar = "",
  direcao = "",
} = {}) => {
  const params = new URLSearchParams();
  params.set("indice", indice);
  params.set("qtde", qtde);
  if (termo) params.set("termo", termo);
  if (ativo !== "" && ativo !== null && ativo !== undefined) params.set("ativo", ativo);
  appendSort(params, ordenar, direcao);
  return api.get(`/v1/clientes?${params.toString()}`);
};
export const getClientById = async (id) => api.get(`/v1/clientes/${id}`);
export const createClient = async (data) => api.post('/v1/clientes', data);
export const updateClient = async (id, data) => api.put(`/v1/clientes/${id}`, data);
export const deleteClient = async (id) => api.delete(`/v1/clientes/${id}`);

// --- Fornecedores (paginado + busca) ---
export const getAllSuppliers = async ({
  indice = 1,
  qtde = 20,
  termo = "",
  ordenar = "",
  direcao = "",
} = {}) => {
  const params = new URLSearchParams();
  params.set("indice", indice);
  params.set("qtde", qtde);
  if (termo) params.set("termo", termo);
  appendSort(params, ordenar, direcao);
  return api.get(`/v1/fornecedores?${params.toString()}`);
};
export const getSupplierById = async (id) => api.get(`/v1/fornecedores/${id}`);
export const createSupplier = async (data) => api.post('/v1/fornecedores', data);
export const updateSupplier = async (id, data) => api.put(`/v1/fornecedores/${id}`, data);
export const deleteSupplier = async (id) => api.delete(`/v1/fornecedores/${id}`);

// --- Transportadoras ---
export const getAllTransportadoras = async ({
  indice = 1,
  qtde = 20,
  termo = '',
  ativo = true,
  ordenar = '',
  direcao = '',
} = {}) => {
  const params = new URLSearchParams();
  params.set('indice', indice);
  params.set('qtde', qtde);
  if (termo) params.set('termo', termo);
  if (ativo !== undefined && ativo !== null) params.set('ativo', String(ativo));
  appendSort(params, ordenar, direcao);
  return api.get(`/v1/transportadoras?${params.toString()}`);
};
export const getTransportadoraById = (id) => api.get(`/v1/transportadoras/${id}`);
export const createTransportadora = (data) => api.post('/v1/transportadoras', data);
export const updateTransportadora = (id, data) => api.put(`/v1/transportadoras/${id}`, data);
export const deleteTransportadora = (id) => api.delete(`/v1/transportadoras/${id}`);

// --- Importação XML NF-e (entrada de estoque) ---
export const previewNfeXmlImport = (estabelecimentoId, file) => {
  const form = new FormData();
  form.append('estabelecimentoId', String(estabelecimentoId));
  form.append('arquivo', file);
  return api.post('/v1/estoque/importar-xml/preview', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};
export const confirmarNfeXmlImport = (payload) =>
  api.post('/v1/estoque/importar-xml/confirmar', payload);
export const getNfesImportadas = (params = {}) =>
  api.get('/v1/estoque/importar-xml', { params });
export const getNfeImportada = (id) =>
  api.get(`/v1/estoque/importar-xml/${id}`);
export const downloadNfeImportadaXml = (id) =>
  api.get(`/v1/estoque/importar-xml/${id}/xml`, { responseType: 'blob' });
export const devolverNfeImportada = (id, payload = {}) =>
  api.post(`/v1/estoque/importar-xml/${id}/devolver`, payload);

export const getNfesDestinatario = (params = {}) =>
  api.get('/v1/fiscal/manifesto', { params });
export const atualizarManifestoNfe = (estabelecimentoId) =>
  api.post('/v1/fiscal/manifesto/atualizar', { estabelecimentoId });
export const buscarNfeDestinatarioPorChave = (estabelecimentoId, chave) =>
  api.post('/v1/fiscal/manifesto/buscar-chave', { estabelecimentoId, chave });
export const manifestarNfeDestinatario = (id, estabelecimentoId, payload) =>
  api.post(`/v1/fiscal/manifesto/${id}/manifestar`, payload, { params: { estabelecimentoId } });
export const getNfeDestinatarioXml = (id, estabelecimentoId) =>
  api.get(`/v1/fiscal/manifesto/${id}/xml`, { params: { estabelecimentoId } });

// --- Estoque ---
export const getStockMovements = async ({
  indice = 1,
  qtde = 20,
  estabelecimentoId = "",
  usuarioId = "",
  produtoId = "",
  fluxo = "",         // "E" | "S"  (o endpoint aceita vazio = ambos)
  tipoId = "",        // 1..7 (Compra, Venda, etc.)
  termo = "",         // busca por produto/usuário
  de = "",            // Date | ISO string
  ate = "",
  pendente = false,   // false=confirmados; true=pendentes do PDV
} = {}) => {
  const p = new URLSearchParams();
  p.set("indice", indice);
  p.set("qtde", qtde);
  if (estabelecimentoId) p.set("estabelecimentoId", estabelecimentoId);
  if (usuarioId) p.set("usuarioId", usuarioId);
  if (produtoId) p.set("produtoId", produtoId);
  if (fluxo) p.set("fluxo", fluxo);
  if (tipoId) p.set("tipoId", tipoId);
  if (termo) p.set("termo", termo);
  if (de) p.set("de", toApiDateStart(de));
  if (ate) p.set("ate", toApiDateEnd(ate));
  p.set("pendente", String(!!pendente));
  return api.get(`/v1/estoque/movimentos?${p.toString()}`);
};

export const createStockEntry = (items) =>
  api.post('/v1/estoque/entrada', items);

export const createStockExit = (items, bloquearNegativo = true) =>
  api.post(`/v1/estoque/saida?bloquearNegativo=${bloquearNegativo}`, items);

export const inventoryAdjust = (items) =>
  api.post('/v1/estoque/inventario', items);

export const reajusteSaldoEstoque = (payload) =>
  api.post('/v1/estoque/reajuste', payload);

export const confirmPendingStockMovements = (ids) =>
  api.post('/v1/estoque/movimentos/confirmar', { ids });

export const cancelPendingStockMovements = (ids) =>
  api.post('/v1/estoque/movimentos/cancelar', { ids });

export const recalcStock = (produtoId = 0) =>
  api.post(`/v1/estoque/recalcular?produtoId=${produtoId}`);

export const getLocaisEstoque = (estabelecimentoId) =>
  api.get('/v1/estoque/locais', {
    params: estabelecimentoId ? { estabelecimentoId } : {},
  });

export const createLocalEstoque = (payload) =>
  api.post('/v1/estoque/locais', payload);

export const updateLocalEstoque = (id, payload) =>
  api.put(`/v1/estoque/locais/${id}`, payload);

export const transferirLocalEstoque = (payload) =>
  api.post('/v1/estoque/locais/transferir', payload);

// --- Transferência de estoque (romaneio) ---
export const getTransferenciasEstoque = (params = {}) => {
  const p = new URLSearchParams();
  if (params.indice) p.set('indice', params.indice);
  if (params.qtde) p.set('qtde', params.qtde);
  if (params.estabelecimentoId) p.set('estabelecimentoId', params.estabelecimentoId);
  if (params.status != null && params.status !== '') p.set('status', params.status);
  if (params.termo) p.set('termo', params.termo);
  return api.get(`/v1/transferencias-estoque?${p.toString()}`);
};

export const getTransferenciaEstoque = (id) =>
  api.get(`/v1/transferencias-estoque/${id}`);

export const createTransferenciaEstoque = (payload) =>
  api.post('/v1/transferencias-estoque', payload);

export const updateTransferenciaEstoque = (id, payload) =>
  api.put(`/v1/transferencias-estoque/${id}`, payload);

export const separarTransferenciaEstoque = (id, payload) =>
  api.post(`/v1/transferencias-estoque/${id}/separar`, payload);

export const concluirTransferenciaEstoque = (id, payload) =>
  api.post(`/v1/transferencias-estoque/${id}/concluir`, payload);

export const cancelarTransferenciaEstoque = (id, payload) =>
  api.post(`/v1/transferencias-estoque/${id}/cancelar`, payload);

export const getTransferenciaRomaneioPdf = (id) =>
  api.get(`/v1/transferencias-estoque/${id}/romaneio`, { responseType: 'blob' });

// --- Relatório situação de estoque ---
export const getEstoqueSituacaoRelatorio = (params = {}) => {
  const p = new URLSearchParams();
  if (params.tipo) p.set('tipo', params.tipo);
  if (params.estabelecimentoId) p.set('estabelecimentoId', params.estabelecimentoId);
  if (params.categoriaId) p.set('categoriaId', params.categoriaId);
  if (params.fornecedorId) p.set('fornecedorId', params.fornecedorId);
  if (params.termo) p.set('termo', params.termo);
  if (params.indice) p.set('indice', params.indice);
  if (params.qtde) p.set('qtde', params.qtde);
  if (params.formato) p.set('formato', params.formato);
  const isCsv = params.formato === 'csv';
  return api.get(
    `/v1/estoque/relatorios/situacao?${p.toString()}`,
    isCsv ? { responseType: 'blob' } : undefined,
  );
};

export const getEstoqueDisponibilidadeRelatorio = (params = {}) => {
  const p = new URLSearchParams();
  if (params.estabelecimentoId) p.set('estabelecimentoId', params.estabelecimentoId);
  if (params.categoriaId) p.set('categoriaId', params.categoriaId);
  if (params.subCategoriaId) p.set('subCategoriaId', params.subCategoriaId);
  if (params.marcaId) p.set('marcaId', params.marcaId);
  if (params.localEstoqueId) p.set('localEstoqueId', params.localEstoqueId);
  if (params.termo) p.set('termo', params.termo);
  if (params.indice) p.set('indice', params.indice);
  if (params.qtde) p.set('qtde', params.qtde);
  if (params.formato) p.set('formato', params.formato);
  const isCsv = params.formato === 'csv';
  return api.get(
    `/v1/estoque/relatorios/disponibilidade?${p.toString()}`,
    isCsv ? { responseType: 'blob' } : undefined,
  );
};

export const buscarPlanejamentoCompras = (payload) =>
  api.post('/v1/estoque/relatorios/planejamento-compras/buscar', payload);

export const calcularPlanejamentoCompras = (payload) =>
  api.post('/v1/estoque/relatorios/planejamento-compras', payload);

export const enviarPlanejamentoComprasEmail = (payload) =>
  api.post('/v1/estoque/relatorios/planejamento-compras/email', payload);

export const enviarPlanejamentoComprasWhatsApp = (payload) =>
  api.post('/v1/estoque/relatorios/planejamento-compras/whatsapp', payload);

// --- Campanhas Promocionais ---
export const getAllCampaigns = async ({
  indice = 1,
  qtde = 20,
  termo = "",
  ativo = "",
  vigente = "",
  ordenar = "",
  direcao = "",
} = {}) => {
  const p = new URLSearchParams();
  p.set("indice", indice);
  p.set("qtde", qtde);
  if (termo) p.set("termo", termo);
  if (ativo !== "") p.set("ativo", ativo);
  if (vigente !== "") p.set("vigente", vigente);
  appendSort(p, ordenar, direcao);
  return api.get(`/v1/campanhas?${p.toString()}`);
};

export const getCampaignById = (id) => api.get(`/v1/campanhas/${id}`);

export const createCampaign = (payload) => api.post(`/v1/campanhas`, payload);

export const updateCampaign = (id, payload) => api.put(`/v1/campanhas/${id}`, payload);

export const deleteCampaign = (id) => api.delete(`/v1/campanhas/${id}`);

export const patchCampaignStatus = (id, ativo) => api.patch(`/v1/campanhas/${id}/status`, null, { params: { ativo }});

// --- PDVs ---

export const getAllPdvs = () => api.get('/v1/pdvs');         // sem paginação no backend (a lista paginará no front)
export const getPdvById = (id) => api.get(`/v1/pdvs/${id}`);
export const createPdv = (payload) => api.post('/v1/pdvs', payload);
export const updatePdv = (id, payload) => api.put(`/v1/pdvs/${id}`, payload);
export const deletePdv = (id) => api.delete(`/v1/pdvs/${id}`);
export const copiarConfigPdv = (origemId, payload) =>
  api.post(`/v1/pdvs/${origemId}/copiar-configuracao`, payload);
export const solicitarCargaBalancaPdv = (id) =>
  api.post(`/v1/pdvs/${id}/solicitar-carga-balanca`);
export const solicitarImpressaoEtiquetasPdv = (id, payload) =>
  api.post(`/v1/pdvs/${id}/solicitar-impressao-etiquetas`, payload);

export const getPdvInstaladores = () => api.get('/v1/pdv/atualizacao/instaladores');

export const downloadPdvInstalador = async (plataforma, build) => {
  const response = await api.get('/v1/pdv/atualizacao/arquivo', {
    params: {
      plataforma,
      build: build || undefined,
      tipo: 'instalador',
    },
    responseType: 'blob',
  });
  const disposition = response.headers['content-disposition'] || '';
  const match = /filename\*?=(?:UTF-8''|")?([^";]+)/i.exec(disposition);
  const nomeArquivo = match ? decodeURIComponent(match[1].replace(/"/g, '')) : null;
  return { blob: response.data, nomeArquivo };
};

// --- TEF remoto / ZenPay ---
export const getTefCobrancasGestao = (params = {}) => {
  const p = { ...params };
  if (p.de) p.de = toApiDateStart(p.de);
  if (p.ate) p.ate = toApiDateEnd(p.ate);
  return api.get('/v1/tef/cobrancas/gestao', { params: p });
};

// --- Formas de Pagamento ---
export const getAllPaymentMethods = () => api.get('/v1/formaspagamentos');
export const getPaymentMethodById = (id) => api.get(`/v1/formaspagamentos/${id}`);
export const createPaymentMethod = (payload) => api.post('/v1/formaspagamentos', payload);
export const updatePaymentMethod = (id, payload) => api.put(`/v1/formaspagamentos/${id}`, payload);
export const deletePaymentMethod = (id) => api.delete(`/v1/formaspagamentos/${id}`);

// --- Convênio (conta corrente do cliente) ---
export const getConvenioSaldo = (clienteId) =>
  api.get(`/v1/convenios/clientes/${clienteId}/saldo`);

export const getConvenioMovimentos = async ({
  indice = 1,
  qtde = 20,
  clienteId = '',
  termo = '',
  tipo = '',
  de = '',
  ate = '',
} = {}) => {
  const p = new URLSearchParams();
  p.set('indice', indice);
  p.set('qtde', qtde);
  if (clienteId) p.set('clienteId', clienteId);
  if (termo) p.set('termo', termo);
  if (tipo) p.set('tipo', tipo);
  if (de) p.set('de', toApiDateStart(de));
  if (ate) p.set('ate', toApiDateEnd(ate));
  return api.get(`/v1/convenios/movimentos?${p.toString()}`);
};

export const createConvenioRecebimento = (payload) =>
  api.post('/v1/convenios/recebimento', payload);

export const deleteConvenioRecebimento = (id) =>
  api.delete(`/v1/convenios/recebimentos/${id}`);

export const getConvenioExtrato = (clienteId, { de, ate } = {}) => {
  const p = new URLSearchParams();
  if (de) p.set('de', de);
  if (ate) p.set('ate', ate);
  return api.get(`/v1/convenios/clientes/${clienteId}/extrato?${p.toString()}`);
};

export const enviarConvenioExtratoWhatsApp = (clienteId, payload) =>
  api.post(`/v1/convenios/clientes/${clienteId}/extrato/whatsapp`, payload);

// --- Vales troca / devoluções cliente ---
export const getDevolucoes = ({
  indice = 1,
  qtde = 20,
  clienteId,
  utilizado,
  termo,
  estabelecimentoId,
  de,
  ate,
} = {}) => {
  const p = new URLSearchParams();
  p.set('indice', indice);
  p.set('qtde', qtde);
  if (clienteId) p.set('clienteId', clienteId);
  if (utilizado === true || utilizado === false) p.set('utilizado', utilizado);
  if (termo) p.set('termo', termo);
  if (estabelecimentoId) p.set('estabelecimentoId', estabelecimentoId);
  if (de) p.set('de', toApiDateStart(de));
  if (ate) p.set('ate', toApiDateEnd(ate));
  return api.get(`/v1/devolucoes?${p.toString()}`);
};

export const getSessoesCaixa = () => api.get('/v1/sessaocaixa');

// --- Integrações PIX ---
export const getAllPixIntegrations = () => api.get('/v1/pixintegracoes');
export const getPixIntegrationById = (id) => api.get(`/v1/pixintegracoes/${id}`);
export const createPixIntegration = (payload) => api.post('/v1/pixintegracoes', payload);
export const updatePixIntegration = (id, payload) => api.put(`/v1/pixintegracoes/${id}`, payload);
export const deletePixIntegration = (id) => api.delete(`/v1/pixintegracoes/${id}`);

// --- Estabelecimentos ---
export const getEstabelecimentos = () => api.get('/v1/estabelecimentos'); // array de filiais
export const getEstabelecimentoById = (id) => api.get(`/v1/estabelecimentos/${id}`);
export const updateEstabelecimento = (id, payload) => api.put(`/v1/estabelecimentos/${id}`, payload);
export const createEstabelecimento = (payload) => api.post('/v1/estabelecimentos', payload);
export const getLicencaStatus = () => api.get('/v1/licenca/status');
export const resolverCodigoMunicipio = (cidade, uf) =>
  api.get('/v1/municipios/codigo', { params: { cidade, uf } });

export const getWhatsAppSessao = (estabelecimentoId) =>
  api.get('/v1/whatsapp/sessao', { params: { estabelecimentoId } });
export const conectarWhatsApp = (estabelecimentoId) =>
  api.post('/v1/whatsapp/sessao/conectar', null, { params: { estabelecimentoId } });
export const desconectarWhatsApp = (estabelecimentoId, apagarSessao = false) =>
  api.post('/v1/whatsapp/sessao/desconectar', { apagarSessao, estabelecimentoId }, {
    params: { estabelecimentoId },
  });
export const enviarWhatsApp = (payload) => api.post('/v1/whatsapp/enviar', payload);
export const getWhatsAppAutomacao = (estabelecimentoId) =>
  api.get('/v1/whatsapp/automacao', { params: { estabelecimentoId } });
export const updateWhatsAppAutomacao = (payload) => api.put('/v1/whatsapp/automacao', payload);

// --- Dashboard real ---
export const getDashboard = ({ de = '', ate = '', estabelecimentoId = '' } = {}) => {
  const p = new URLSearchParams();
  if (de) p.set('de', toApiDateStart(de));
  if (ate) p.set('ate', toApiDateEnd(ate));
  if (estabelecimentoId) p.set('estabelecimentoId', estabelecimentoId);
  return api.get(`/v1/dashboard?${p.toString()}`);
};

// --- Gestão de sessões de caixa ---
export const getCashSessions = ({
  indice = 1,
  qtde = 20,
  de = '',
  ate = '',
  estabelecimentoId = '',
  pdvId = '',
  usuarioId = '',
  status = '',
} = {}) => {
  const p = new URLSearchParams({ indice, qtde });
  if (de) p.set('de', toApiDateStart(de));
  if (ate) p.set('ate', toApiDateEnd(ate));
  if (estabelecimentoId) p.set('estabelecimentoId', estabelecimentoId);
  if (pdvId) p.set('pdvId', pdvId);
  if (usuarioId) p.set('usuarioId', usuarioId);
  if (status) p.set('status', status);
  return api.get(`/v1/caixa/sessoes?${p.toString()}`);
};

export const getCashSession = (id) => api.get(`/v1/caixa/sessoes/${id}`);
export const closeCashSession = (id, conferencias) =>
  api.post(`/v1/caixa/sessoes/${id}/fechar`, { conferencias });
export const createCashMovement = (sessionId, payload) =>
  api.post(`/v1/caixa/sessoes/${sessionId}/movimentos`, payload);
export const updateCashMovement = (sessionId, movementId, payload) =>
  api.put(`/v1/caixa/sessoes/${sessionId}/movimentos/${movementId}`, payload);

// --- Pré-vendas / cartões ---
export const getPreVendas = ({
  indice = 1,
  qtde = 20,
  de = '',
  ate = '',
  estabelecimentoId = '',
  status = '',
  tipo = '',
  termo = '',
} = {}) => {
  const p = new URLSearchParams({ indice, qtde });
  if (de) p.set('de', toApiDateStart(de));
  if (ate) p.set('ate', toApiDateEnd(ate));
  if (estabelecimentoId) p.set('estabelecimentoId', estabelecimentoId);
  if (status) p.set('status', status);
  if (tipo) p.set('tipo', tipo);
  if (termo) p.set('termo', termo);
  return api.get(`/v1/prevendas?${p.toString()}`);
};
export const getPreVenda = (id) => api.get(`/v1/prevendas/${id}`);
export const getPreVendaByCodigo = (codigo, { estabelecimentoId = '', apenasAbertas = true } = {}) => {
  const p = new URLSearchParams();
  if (estabelecimentoId) p.set('estabelecimentoId', estabelecimentoId);
  p.set('apenasAbertas', String(apenasAbertas));
  const qs = p.toString();
  return api.get(
    `/v1/prevendas/codigo/${encodeURIComponent(codigo)}${qs ? `?${qs}` : ''}`
  );
};
export const cancelPreVenda = (id, payload) =>
  api.post(`/v1/prevendas/${id}/cancelar`, payload);

// --- Orçamentos / pedidos comerciais ---
export const getPedidosComerciais = ({
  indice = 1,
  qtde = 20,
  estabelecimentoId = '',
  status = '',
  tipo = '',
  termo = '',
  dataInicio = '',
  dataFim = '',
} = {}) => {
  const p = new URLSearchParams({ indice, qtde });
  if (estabelecimentoId) p.set('estabelecimentoId', estabelecimentoId);
  if (status) p.set('status', status);
  if (tipo) p.set('tipo', tipo);
  if (termo) p.set('termo', termo);
  if (dataInicio) p.set('dataInicio', dataInicio);
  if (dataFim) p.set('dataFim', dataFim);
  return api.get(`/v1/pedidos-comerciais?${p.toString()}`);
};
export const getPedidoComercial = (id) => api.get(`/v1/pedidos-comerciais/${id}`);
export const createPedidoComercial = (payload) =>
  api.post('/v1/pedidos-comerciais', payload);
export const updatePedidoComercial = (id, payload) =>
  api.put(`/v1/pedidos-comerciais/${id}`, payload);
export const cancelPedidoComercial = (id, payload = {}) =>
  api.post(`/v1/pedidos-comerciais/${id}/cancelar`, payload);
export const converterPedidoComercial = (id) =>
  api.post(`/v1/pedidos-comerciais/${id}/converter-pedido`);
export const getPedidoComercialImpressao = (id) =>
  api.get(`/v1/pedidos-comerciais/${id}/impressao`);
export const getPedidoComercialNfePayload = (id) =>
  api.get(`/v1/pedidos-comerciais/${id}/nfe-payload`);
export const faturarPedidoComercial = (id, payload = {}) =>
  api.post(`/v1/pedidos-comerciais/${id}/faturar`, payload);
export const atualizarPrecosPedidoComercial = (id) =>
  api.post(`/v1/pedidos-comerciais/${id}/atualizar-precos`);

// --- Pedidos de compra (ADD_COMPRA) ---
export const getPedidosCompra = ({
  indice = 1,
  qtde = 20,
  estabelecimentoId = '',
  status = '',
  fornecedorId = '',
  termo = '',
} = {}) => {
  const p = new URLSearchParams({ indice, qtde });
  if (estabelecimentoId) p.set('estabelecimentoId', estabelecimentoId);
  if (status) p.set('status', status);
  if (fornecedorId) p.set('fornecedorId', fornecedorId);
  if (termo) p.set('termo', termo);
  return api.get(`/v1/pedidos-compra?${p.toString()}`);
};
export const getPedidoCompra = (id) => api.get(`/v1/pedidos-compra/${id}`);
export const createPedidoCompra = (payload) => api.post('/v1/pedidos-compra', payload);
export const updatePedidoCompra = (id, payload) => api.put(`/v1/pedidos-compra/${id}`, payload);
export const cancelPedidoCompra = (id, payload = {}) =>
  api.post(`/v1/pedidos-compra/${id}/cancelar`, payload);
export const aprovarPedidoCompra = (id) => api.post(`/v1/pedidos-compra/${id}/aprovar`);
export const gerarLinkPedidoCompra = (id) => api.post(`/v1/pedidos-compra/${id}/gerar-link`);
export const invalidarLinkPedidoCompra = (id) =>
  api.post(`/v1/pedidos-compra/${id}/invalidar-link`);
export const marcarRecebidoPedidoCompra = (id, payload = {}) =>
  api.post(`/v1/pedidos-compra/${id}/marcar-recebido`, payload);

export const unlockPedidoCompraPublico = (slug, token, codigoAcesso) =>
  api.post(`/v1/public/${encodeURIComponent(slug)}/pedidos-compra/${encodeURIComponent(token)}/unlock`, {
    codigoAcesso,
  });
export const getPedidoCompraPublico = (slug, token, accessToken) =>
  api.get(`/v1/public/${encodeURIComponent(slug)}/pedidos-compra/${encodeURIComponent(token)}`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
export const updatePedidoCompraPublicoItens = (slug, token, accessToken, itens) =>
  api.put(
    `/v1/public/${encodeURIComponent(slug)}/pedidos-compra/${encodeURIComponent(token)}/itens`,
    { itens },
    { headers: { Authorization: `Bearer ${accessToken}` } },
  );
export const enviarPedidoCompraPublico = (slug, token, accessToken) =>
  api.post(
    `/v1/public/${encodeURIComponent(slug)}/pedidos-compra/${encodeURIComponent(token)}/enviar`,
    {},
    { headers: { Authorization: `Bearer ${accessToken}` } },
  );

// --- Relatórios gerenciais ---
const reportParams = (filters = {}) => {
  const p = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value === '' || value == null) return;
    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (item === '' || item == null) return;
        p.append(key, item);
      });
      return;
    }
    if (key === 'de') p.set(key, toApiDateStart(value));
    else if (key === 'ate') p.set(key, toApiDateEnd(value));
    else p.set(key, value);
  });
  return p.toString();
};

export const getSalesReport = (filters = {}) =>
  api.get(`/v1/relatorios/vendas?${reportParams(filters)}`);
export const getSaleReportDetail = (id) =>
  api.get(`/v1/relatorios/vendas/${id}`);
export const getSalesBySellerReport = (filters = {}) =>
  api.get(`/v1/relatorios/vendas-por-vendedor?${reportParams(filters)}`);
export const getCommissionBySellerReport = (filters = {}) =>
  api.get(`/v1/relatorios/comissao-por-vendedor?${reportParams(filters)}`);
export const alterarFormasPagamentoVenda = (id, formas) =>
  api.post(`/v1/vendas/${id}/alterar-formas-pagamento`, formas);
export const cancelSale = (id, motivo) =>
  api.post(`/v1/vendas/${id}/cancelar`, { motivo: motivo || '' });
export const enviarWhatsAppVenda = (id, telefone) =>
  api.post(`/v1/vendas/${id}/whatsapp`, { telefone: telefone || '' });
export const getPaymentMethodsReport = (filters = {}) =>
  api.get(`/v1/relatorios/formas-pagamento?${reportParams(filters)}`);
export const getProductsReport = (filters = {}) =>
  api.get(`/v1/relatorios/produtos?${reportParams(filters)}`);
export const getProductsBySellerReport = (filters = {}) =>
  api.get(`/v1/relatorios/produtos-por-vendedor?${reportParams(filters)}`);
export const getConvenioReport = (filters = {}) =>
  api.get(`/v1/relatorios/convenios?${reportParams(filters)}`);
export const getCashbackReport = (filters = {}) =>
  api.get(`/v1/relatorios/cashback?${reportParams(filters)}`);
export const getCancellationReport = (filters = {}) =>
  api.get(`/v1/relatorios/cancelamentos?${reportParams(filters)}`);
export const getAbcCurveReport = (filters = {}) =>
  api.get(`/v1/relatorios/curva-abc?${reportParams(filters)}`);

export const getFiscalDocumentsReport = (filters = {}) =>
  api.get(`/v1/relatorios/documentos-fiscais?${reportParams(filters)}`);
export const downloadFiscalDocumentsXmlZip = (filters = {}) =>
  api.get(`/v1/relatorios/documentos-fiscais/xml-zip?${reportParams(filters)}`, { responseType: 'blob' });
export const sendFiscalDocumentsEmail = (payload) =>
  api.post('/v1/relatorios/documentos-fiscais/email', payload);
export const sendFiscalDocumentsWhatsApp = (payload) =>
  api.post('/v1/relatorios/documentos-fiscais/whatsapp', payload);
export const sendFiscalDocumentsContador = (payload) =>
  api.post('/v1/relatorios/documentos-fiscais/enviar-contador', payload);

export const getAuditLogs = (filters = {}) =>
  api.get(`/v1/logs?${reportParams(filters)}`);
export const getAuditEntities = () =>
  api.get('/v1/logs/entidades');

// --- Utilitários ---
export const getScaleLoadProducts = () => api.get('/v1/utilitarios/carga-balanca/produtos');
export const downloadScaleLoad = (marca = 'todas') =>
  api.get(`/v1/utilitarios/carga-balanca?marca=${encodeURIComponent(marca)}`, {
    responseType: 'blob',
  });
export const downloadProductExcelModel = () =>
  api.get('/v1/utilitarios/produtos/excel/modelo', { responseType: 'blob' });
export const exportProductsExcel = () =>
  api.get('/v1/utilitarios/produtos/excel/exportar', { responseType: 'blob' });
export const importProductsExcel = (file, { filialPrecosId } = {}) => {
  const form = new FormData();
  form.append('arquivo', file);
  const params = new URLSearchParams();
  if (filialPrecosId) params.set('filialPrecosId', String(filialPrecosId));
  const qs = params.toString();
  return api.post(
    `/v1/utilitarios/produtos/excel/importar${qs ? `?${qs}` : ''}`,
    form,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
    },
  );
};
export const validateProductsExcel = (file, { filialPrecosId } = {}) => {
  const form = new FormData();
  form.append('arquivo', file);
  const params = new URLSearchParams();
  if (filialPrecosId) params.set('filialPrecosId', String(filialPrecosId));
  const qs = params.toString();
  return api.post(
    `/v1/utilitarios/produtos/excel/validar${qs ? `?${qs}` : ''}`,
    form,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
    },
  );
};
export const importProductsExcelBatch = ({ token, tamanhoLote = 40 }) =>
  api.post('/v1/utilitarios/produtos/excel/importar/lote', {
    token,
    tamanhoLote,
  });
export const downloadClientExcelModel = () =>
  api.get('/v1/utilitarios/clientes/excel/modelo', { responseType: 'blob' });
export const exportClientsExcel = () =>
  api.get('/v1/utilitarios/clientes/excel/exportar', { responseType: 'blob' });
export const validateClientsExcel = (file) => {
  const form = new FormData();
  form.append('arquivo', file);
  return api.post('/v1/utilitarios/clientes/excel/validar', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};
export const importClientsExcel = ({ token, file } = {}) => {
  if (token) {
    return api.post(
      `/v1/utilitarios/clientes/excel/importar?token=${encodeURIComponent(token)}`,
    );
  }
  const form = new FormData();
  form.append('arquivo', file);
  return api.post('/v1/utilitarios/clientes/excel/importar', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};
export const getLabelModels = () => api.get('/v1/utilitarios/modelos-etiquetas');
export const createLabelModel = (payload) =>
  api.post('/v1/utilitarios/modelos-etiquetas', payload);
export const updateLabelModel = (id, payload) =>
  api.put(`/v1/utilitarios/modelos-etiquetas/${id}`, payload);
export const deleteLabelModel = (id) =>
  api.delete(`/v1/utilitarios/modelos-etiquetas/${id}`);
export const previewLabels = (payload) =>
  api.post('/v1/utilitarios/etiquetas/preview', payload);
export const generateLabelRaw = (payload) =>
  api.post('/v1/utilitarios/etiquetas/raw', payload);
export const getLabelMacros = () => api.get('/v1/utilitarios/etiquetas/macros');

export const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};


// --- Log de Auditoria ---
export const getAllLogs = async (filters = {}) =>
  api.get(`/v1/logs?${reportParams(filters)}`);

export default api;
